import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import CollegeFilters from "../components/sections/colleges/CollegeFilters";
import FeaturedCollegeStrip from "../components/sections/colleges/FeaturedCollegeStrip";
import StateGroupSection from "../components/sections/colleges/StateGroupSection";
import PageMeta from "../components/ui/PageMeta";
import { pageCtaContent } from "../data/ctaContent";
import editableData from "../data/siteEditable.json";
import {
  allColleges,
  collegeOverview,
  collegesPageContent,
  collegePathwayFilters,
  collegeStateFilters,
  collegeStateOrder,
  featuredColleges
} from "../data/colleges";

const matchesPathway = (college, pathway) => {
  if (pathway === "all") {
    return true;
  }

  if (pathway === "featured") {
    return college.featured;
  }

  return college.categoryTags.includes(pathway);
};

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1400&q=80";

function Colleges() {
  const cta = pageCtaContent.colleges;
  const collegeNumbers = editableData.numbers?.collegesPage || {};
  const statesCovered = collegeNumbers.statesCovered ?? collegeOverview.stateCount;
  const collegeProfiles = collegeNumbers.collegeProfiles ?? collegeOverview.collegeCount;
  const featuredInstitutions = collegeNumbers.featuredInstitutions ?? collegeOverview.featuredCount;
  const [selectedState, setSelectedState] = useState("all");
  const [selectedPathway, setSelectedPathway] = useState("all");

  const filteredColleges = useMemo(
    () =>
      allColleges.filter((college) => {
        const byState = selectedState === "all" || college.state === selectedState;
        const byPathway = matchesPathway(college, selectedPathway);
        return byState && byPathway;
      }),
    [selectedPathway, selectedState]
  );

  const groupedColleges = useMemo(
    () =>
      collegeStateOrder
        .map((state) => ({
          state,
          colleges: filteredColleges.filter((college) => college.state === state)
        }))
        .filter((group) => group.colleges.length > 0),
    [filteredColleges]
  );

  const stateCounts = useMemo(() => {
    const counts = { all: allColleges.filter((college) => matchesPathway(college, selectedPathway)).length };

    collegeStateOrder.forEach((state) => {
      counts[state] = allColleges.filter(
        (college) => college.state === state && matchesPathway(college, selectedPathway)
      ).length;
    });

    return counts;
  }, [selectedPathway]);

  const pathwayCounts = useMemo(() => {
    const counts = {};

    collegePathwayFilters.forEach((filter) => {
      counts[filter.value] = allColleges.filter((college) => {
        const byState = selectedState === "all" || college.state === selectedState;
        return byState && matchesPathway(college, filter.value);
      }).length;
    });

    return counts;
  }, [selectedState]);

  return (
    <>
      <PageMeta title={collegesPageContent.meta.title} description={collegesPageContent.meta.description} />

      <section className="relative overflow-hidden bg-white pb-24 pt-40 sm:pt-44 lg:pt-48">
        <div className="absolute inset-0 opacity-70">
          <div
            className="h-full w-full"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(116,119,127,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(116,119,127,0.08) 1px, transparent 1px)",
              backgroundSize: "60px 60px"
            }}
          />
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-6 lg:px-10">
          <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
            <div>
              <p className="inline-flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-gold-strong">
                <span className="h-px w-10 bg-brand-gold-strong/70" />
                {collegesPageContent.hero.badge}
              </p>

              <h1 className="mt-7 text-4xl font-semibold leading-[1.02] tracking-tight text-brand-navy sm:text-6xl lg:text-[4.3rem]">
                {collegesPageContent.hero.title}
              </h1>

              <p className="mt-7 max-w-2xl text-base leading-relaxed text-brand-charcoal/76 sm:text-lg">
                {collegesPageContent.hero.description}
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-brand-navy px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:bg-brand-gold-strong"
                >
                  Book Counselling
                </Link>
                <a
                  href="#archive-explorer"
                  className="inline-flex items-center gap-2 border border-brand-line bg-white px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] text-brand-navy transition-colors hover:border-brand-gold/50 hover:text-brand-gold-strong"
                >
                  Explore Archive
                </a>
              </div>

              <ul className="mt-9 space-y-2.5">
                {collegesPageContent.hero.highlights.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-brand-charcoal/76">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold-strong" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative mx-auto w-full max-w-md lg:mx-0">
              <div className="absolute -right-6 -top-6 h-full w-full border-t-2 border-r-2 border-brand-gold/25" />
              <article className="relative z-10 overflow-hidden border-r-[8px] border-b-[8px] border-brand-gold-strong shadow-[0_34px_60px_-30px_rgba(10,31,68,0.45)]">
                <img
                  src={HERO_IMAGE}
                  alt="College campus and counselling setting"
                  className="h-[480px] w-full object-cover"
                  loading="eager"
                />
              </article>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto grid w-full max-w-7xl gap-10 px-5 sm:grid-cols-3 sm:px-6 lg:px-10">
          <article>
            <p className="text-6xl font-semibold leading-none text-brand-navy sm:text-7xl">
              {String(statesCovered).padStart(2, "0")}
            </p>
            <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-gold-strong">
              States Covered
            </p>
            <p className="mt-2 text-sm leading-relaxed text-brand-charcoal/74">
              Organized for quick regional scanning and shortlist planning.
            </p>
          </article>

          <article>
            <p className="text-6xl font-semibold leading-none text-brand-navy sm:text-7xl">
              {collegeProfiles}
            </p>
            <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-gold-strong">
              College Profiles
            </p>
            <p className="mt-2 text-sm leading-relaxed text-brand-charcoal/74">
              Structured to support counselling-led comparison discussions.
            </p>
          </article>

          <article>
            <p className="text-6xl font-semibold leading-none text-brand-navy sm:text-7xl">
              {featuredInstitutions}
            </p>
            <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-gold-strong">
              Featured Institutions
            </p>
            <p className="mt-2 text-sm leading-relaxed text-brand-charcoal/74">
              Commonly explored options across major admission pathways.
            </p>
          </article>
        </div>
      </section>

      <section className="relative overflow-hidden bg-brand-sky/25 py-20 sm:py-24">
        <div className="absolute inset-0 opacity-45">
          <div
            className="h-full w-full"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(116,119,127,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(116,119,127,0.08) 1px, transparent 1px)",
              backgroundSize: "60px 60px"
            }}
          />
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-6 lg:px-10">
          <FeaturedCollegeStrip colleges={featuredColleges.slice(0, 9)} />
        </div>
      </section>

      <section id="archive-explorer" className="bg-white py-20 sm:py-24">
        <div className="mx-auto w-full max-w-7xl px-5 sm:px-6 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-gold-strong">
                Archive Explorer
              </p>
              <h2 className="mt-3 text-3xl font-semibold text-brand-navy sm:text-4xl">
                Browse colleges with practical filters
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-brand-charcoal/76">
                Use filters to narrow relevant options, then connect with TheCareerSpace for profile-based shortlisting.
              </p>
            </div>
            <div className="border-b border-brand-line pb-2 text-xs font-semibold uppercase tracking-[0.14em] text-brand-charcoal/70">
              Showing {filteredColleges.length} of {allColleges.length} colleges
            </div>
          </div>

          <div className="mt-8">
            <CollegeFilters
              stateFilters={collegeStateFilters}
              pathwayFilters={collegePathwayFilters}
              selectedState={selectedState}
              selectedPathway={selectedPathway}
              onStateChange={setSelectedState}
              onPathwayChange={setSelectedPathway}
              stateCounts={stateCounts}
              pathwayCounts={pathwayCounts}
              filteredCount={filteredColleges.length}
              totalCount={allColleges.length}
              onReset={() => {
                setSelectedState("all");
                setSelectedPathway("all");
              }}
            />
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-brand-sky/25 py-20 sm:py-24">
        <div className="absolute inset-0 opacity-45">
          <div
            className="h-full w-full"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(116,119,127,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(116,119,127,0.08) 1px, transparent 1px)",
              backgroundSize: "60px 60px"
            }}
          />
        </div>

        <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-6 lg:px-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${selectedState}-${selectedPathway}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
              className="space-y-14"
            >
              {groupedColleges.length === 0 ? (
                <div className="border border-brand-line bg-white p-10 text-center shadow-card">
                  <h3 className="text-2xl font-semibold text-brand-navy">No colleges match this filter combination.</h3>
                  <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-brand-charcoal/76">
                    Try a different state or pathway, or reset filters to explore the full college list again.
                  </p>
                </div>
              ) : (
                groupedColleges.map((group, index) => (
                  <StateGroupSection
                    key={group.state}
                    state={group.state}
                    colleges={group.colleges}
                    sectionIndex={index + 1}
                  />
                ))
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      <section className="bg-brand-navy-deep py-24 sm:py-28">
        <div className="mx-auto w-full max-w-7xl px-5 sm:px-6 lg:px-10">
          <div className="text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-gold">
              Our Archival Integrity
            </p>
            <h2 className="mt-4 text-4xl font-semibold text-white sm:text-5xl">
              Families use this page to prepare better counselling discussions
            </h2>
          </div>
          <div className="mt-12 grid gap-px overflow-hidden bg-white/12 md:grid-cols-3">
            {collegesPageContent.trustCards.map((item) => (
              <article key={item.title} className="bg-brand-navy px-7 py-10 text-center sm:px-10">
                <h3 className="text-lg font-semibold uppercase tracking-[0.08em] text-white">{item.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-white">{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-white py-24 sm:py-28">
        <div className="relative mx-auto w-full max-w-5xl px-5 text-center sm:px-6 lg:px-10">
          <h2 className="text-4xl font-semibold leading-tight text-brand-navy sm:text-5xl lg:text-6xl">
            {cta.title}
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-brand-charcoal/78 sm:text-lg">
            {cta.description}
          </p>
          <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to={cta.primaryCta.to}
              className="inline-flex items-center justify-center bg-brand-navy px-10 py-4 text-xs font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:bg-brand-gold-strong"
            >
              {cta.primaryCta.label}
            </Link>
            <Link
              to={cta.secondaryCta.to}
              className="inline-flex items-center gap-2 border border-brand-navy/20 px-10 py-4 text-xs font-semibold uppercase tracking-[0.16em] text-brand-navy transition-colors hover:border-brand-gold hover:text-brand-gold-strong"
            >
              {cta.secondaryCta.label}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

export default Colleges;
